import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-analytics-charts/react-analytics-charts/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { Props } from "docz";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>PageViewsPerPathChart</h1>
    <h2 {...{
      "id": "description"
    }}>{`Description`}</h2>
    <p>{`This `}<a parentName="p" {...{
        "href": "/react-analytics-charts/TableChart"
      }}>{`TableChart`}</a>{` shows the number of pageviews for each page path in the date range specified (number of days ago, until today).`}</p>
    <p>{`This chart is useful for seeing which pages and posts are the most popular.`}</p>
    <p>{`Refer to the `}<a parentName="p" {...{
        "href": "/react-analytics-charts/charts"
      }}>{`Charts Overview`}</a>{` for a list of all charts available.`}</p>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {
  AnalyticsDashboard,
  PageViewsPerPathChart,
} from "react-analytics-charts";
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<AnalyticsDashboard
  authOptions={{ clientId }}
  renderCharts={(gapi, viewId) => {
    return <PageViewsPerPathChart gapi={gapi} viewId={viewId} days={28} />;
  }}
/>
`}</code></pre>
    <h2 {...{
      "id": "props"
    }}>{`Props`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`gapi`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`GoogleAnalyticsEmbedAPI`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Required.`}</strong>{` The ready and authorized `}<a parentName="td" {...{
              "href": "https://justinmahar.github.io/react-use-analytics-api/"
            }}>{`Google Analytics Embed API`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`viewId`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Required.`}</strong>{` View ID for the view the chart pertains to. See `}<a parentName="td" {...{
              "href": "/react-analytics-charts/ViewSelector"
            }}>{`ViewSelector`}</a>{` for more information.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`days`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. Number of days the chart shows data for. Defaults to `}<inlineCode parentName="td">{`28`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`container`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. HTML element ID for the container to which the `}<a parentName="td" {...{
              "href": "https://justinmahar.github.io/react-use-analytics-api/"
            }}>{`Google Analytics Embed API`}</a>{` renders. One will be created if no value is provided.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`options`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/react-analytics-charts/TableChart#chart-options"
            }}>{`TableChartOptions`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. The options for this chart have been preconfigured, but you can add or override any `}<a parentName="td" {...{
              "href": "/react-analytics-charts/TableChart#chart-options"
            }}>{`TableChartOptions`}</a>{` you'd like here, such as the `}<inlineCode parentName="td">{`width`}</inlineCode>{` or sorting.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`query`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/react-analytics-charts/DataChart#query-prop"
            }}><inlineCode parentName="a">{`Query`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. The `}<a parentName="td" {...{
              "href": "/react-analytics-charts/DataChart#query-prop"
            }}>{`query`}</a>{` for this chart has been preconfigured, but you can override any `}<a parentName="td" {...{
              "href": "/react-analytics-charts/DataChart#query-prop"
            }}>{`query properties`}</a>{` you'd like here.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`This component also supports `}<strong parentName="p">{`all `}<inlineCode parentName="strong">{`div`}</inlineCode>{` props`}</strong>{`, such as `}<inlineCode parentName="p">{`className`}</inlineCode>{`, `}<inlineCode parentName="p">{`style`}</inlineCode>{`, and `}<inlineCode parentName="p">{`onClick`}</inlineCode>{`.`}</p>
    <p>{`If you have more than one of this chart on the page, be sure to specify a unique `}<inlineCode parentName="p">{`container`}</inlineCode>{` ID.`}</p>
    <h2 {...{
      "id": "data-queried"
    }}>{`Data Queried`}</h2>
    <p>{`This query's date range spans from a `}<inlineCode parentName="p">{`start-date`}</inlineCode>{` of `}<inlineCode parentName="p">{`NdaysAgo`}</inlineCode>{`, where `}<inlineCode parentName="p">{`N`}</inlineCode>{` is the `}<inlineCode parentName="p">{`days`}</inlineCode>{` prop provided, until an `}<inlineCode parentName="p">{`end-date`}</inlineCode>{` of `}<inlineCode parentName="p">{`today`}</inlineCode>{`. See `}<a parentName="p" {...{
        "href": "/react-analytics-charts/DataChart"
      }}>{`DataChart`}</a>{` for more information on queries.`}</p>
    <h3 {...{
      "id": "metrics"
    }}>{`Metrics`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ga:pageviews`}</inlineCode></li>
    </ul>
    <h3 {...{
      "id": "dimensions"
    }}>{`Dimensions`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ga:pagePath`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "customizing"
    }}>{`Customizing`}</h2>
    <p>{`This chart has been preconfigured for ease of use, but you can customize it by providing a `}<inlineCode parentName="p">{`query`}</inlineCode>{` or `}<inlineCode parentName="p">{`options`}</inlineCode>{` prop.`}</p>
    <p>{`The properties you specify in either prop object will override the ones used by this chart.`}</p>
    <p>{`This means you can cherry-pick the properties you'd like to override, such as the `}<inlineCode parentName="p">{`metrics`}</inlineCode>{` in the `}<inlineCode parentName="p">{`query`}</inlineCode>{`, or the `}<inlineCode parentName="p">{`width`}</inlineCode>{` in `}<inlineCode parentName="p">{`options`}</inlineCode>{`, without having to create a completely `}<a parentName="p" {...{
        "href": "/react-analytics-charts/charts#custom-charts"
      }}>{`custom chart`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      